import { orderedSp0070Payload } from '../config/sp0070-contract.js';
import { CHARGE_ROLE } from './codes.js';
import { DESIGN_SHEET_STATUS } from '../types/design-sheet.js';
import { isProvisionalModelCd } from './model-code.js';
import { subMaterialSapReadiness } from './sub-material-library.js';
import { sheetManualCommerceIssues } from './planner-style.js';
import { SHEET_SECTION, STAGE } from './sheet-sections.js';
/** SAP preparation belongs to the designer and pattern maker; factory delivery is independent. */
export const SAP_PREPARATION_STAGES = [
    { section: SHEET_SECTION.DESIGN, stage: STAGE.STEP_DONE },
    { section: SHEET_SECTION.MATERIAL, stage: STAGE.DESIGNER_DONE },
    { section: SHEET_SECTION.MATERIAL, stage: STAGE.TRIM_TECHNICAL_DONE },
    { section: SHEET_SECTION.MATERIAL, stage: STAGE.USAGE_ENTERED },
    { section: SHEET_SECTION.PATTERN, stage: STAGE.BASE_SIZE_DONE },
    { section: SHEET_SECTION.PATTERN, stage: STAGE.SIZE_SPEC_DONE },
];
/** SP0070 sender payload follows the supplied WSDL, including mandatory empty nodes.
 * Source: docs/reference/sap/SI_SP0070_ModelInfo_EADS2FDV_SOService.wsdl.
 * ABAP receiver table names in eads-catalog.json are not SOAP element names.
 */
/** 缺少历史生产批次时使用 00；不把委托书 version 当作生产次数 */
export const SP0070_SEQ = '00';
/** 图片类型。样例报文里 FLAT_SKETCH 是 F；D / G 是按用途推的，待 CE2 确认 */
export const SP0070_IKIND = {
    FLAT_SKETCH: 'F',
    DETAIL_IMAGE: 'D',
    FG_IMAGE: 'G',
};
/** 传 SAP 的商品图：图示图稿 + 详细图片 + 成品实物图，按序号排 */
export const SP0070_IMAGE_KINDS = ['FLAT_SKETCH', 'DETAIL_IMAGE', 'FG_IMAGE'];
// ---------------------------------------------------------------------------
// 传送预检
// ---------------------------------------------------------------------------
export const SAP_TRANSFER_BLOCKER = {
    /** 只有审批通过的单子能传 */
    STATUS: 'STATUS',
    /** 品牌／中分类／年度／季节缺失的编号仍拦截；仅销售月 XX 不阻塞（2026-10-24）。 */
    MODEL_CD_PROVISIONAL: 'MODEL_CD_PROVISIONAL',
    /** 没有 ColorWay */
    NO_COLORWAY: 'NO_COLORWAY',
    /** 没有面料行 */
    NO_MATERIAL: 'NO_MATERIAL',
    /** 辅料代码还不能传 SAP（临时代码 / 待建档 / 停用未建档） */
    TRIM_NOT_READY: 'TRIM_NOT_READY',
    TECHPACK_NOT_CONFIRMED: 'TECHPACK_NOT_CONFIRMED',
    PREPARATION_NOT_COMPLETE: 'PREPARATION_NOT_COMPLETE',
    DEMO: 'DEMO',
};
/** 新演示单也使用统一编号；以不可编辑的来源保留演示保护，兼容旧演示编号。 */
export function isDemoSheet(sheet) {
    return sheet.source === 'DEMO' || sheet.modelCd.startsWith('ND-DEMO-') || Boolean(sheet.remark?.startsWith('[DEMO]'));
}
/**
 * 传 SAP 前的预检。返回全部阻塞项，空数组 = 可以传。
 *
 * 注意预检只判「这张单本身能不能传」，不管网络与账号 —— 那些是 callEads 的事。
 */
export function sheetSapTransferReadiness(detail, findSubMaterial) {
    const blockers = [];
    for (const issue of sheetManualCommerceIssues(detail))
        blockers.push({ code: SAP_TRANSFER_BLOCKER.TECHPACK_NOT_CONFIRMED, message: `@${issue}` });
    if (isDemoSheet(detail))
        blockers.push({ code: SAP_TRANSFER_BLOCKER.DEMO, message: '@server.demoNoTransfer' });
    if (SAP_PREPARATION_STAGES.some(required => !detail.sectionStages?.some(s => s.section === required.section && s.stage === required.stage && s.completed))) {
        blockers.push({ code: SAP_TRANSFER_BLOCKER.PREPARATION_NOT_COMPLETE, message: '@server.sapPreparationIncomplete' });
    }
    if (detail.status !== DESIGN_SHEET_STATUS.APPROVED) {
        blockers.push({
            code: SAP_TRANSFER_BLOCKER.STATUS,
            message: `只有「已批准」的委托书能传 SAP，当前为「${detail.status}」`,
        });
    }
    if (isProvisionalModelCd(detail.modelCd)) {
        blockers.push({
            code: SAP_TRANSFER_BLOCKER.MODEL_CD_PROVISIONAL,
            message: `委托编号 ${detail.modelCd} 为临时或不合规编号，须先补齐编号资料并生成正式编号才能传 SAP`,
        });
    }
    if (detail.colorways.filter((c) => c.isUsed).length === 0) {
        blockers.push({ code: SAP_TRANSFER_BLOCKER.NO_COLORWAY, message: '没有启用的 ColorWay（配色）行' });
    }
    if (detail.materials.length === 0) {
        blockers.push({ code: SAP_TRANSFER_BLOCKER.NO_MATERIAL, message: '没有面料（主材料）行' });
    }
    const trimReadiness = subMaterialSapReadiness(detail.subMaterials, findSubMaterial);
    const blocking = trimReadiness.issues.filter((i) => i.blocking);
    if (blocking.length > 0) {
        const detailText = blocking
            .map((i) => `第${i.seq}行 ${i.code ?? '（未填代码）'}`)
            .join('、');
        blockers.push({
            code: SAP_TRANSFER_BLOCKER.TRIM_NOT_READY,
            message: `有 ${blocking.length} 行辅料未通过本地 SAP 就绪检查（代码缺失或临时代码 / SAP 物料号未确认 / 待建档或同步失败 / 已停用未建档）：${detailText}`,
        });
    }
    return blockers;
}
// ---------------------------------------------------------------------------
// 报文组装
// ---------------------------------------------------------------------------
const s = (v) => v === null || v === undefined ? '' : String(v).trim();
/** 空串/null 一律不包 —— 组装器只产出「确定要传的值」，缺什么都用空标签 */
const num = (v) => {
    const t = s(v);
    return t === '' ? '' : String(Number(t));
};
/** 辅料 BOM 项目号：KA00、KA01 …（样例报文 POSNR = KA00） */
const posnrOf = (seq) => `KA${String(seq).padStart(2, '0')}`;
export function buildSp0070Payload(detail, images, resolveSubMaterialNumber = code => code, sysGBN = '') {
    const key = { StyleCD: s(detail.styleCd), StyleCDSeq: s(detail.batchNo) || SP0070_SEQ };
    const head = {
        ...key, AttachmentFilePath: s(images[0]?.url), ModelCD: s(detail.modelCd), ItemCD: s(detail.itemCd),
        CAD_NO: s(detail.cadNo), TechPack_NO: s(detail.charges.find(c => c.role === CHARGE_ROLE.TECHPACK)?.empNo),
        DesignerSAPYN: '0', TechPackSAPYN: '0', FileSize: images[0] ? String(images[0].sizeBytes) : '',
        ORGFILE: s(images[0]?.filename), SysGBN: sysGBN,
    };
    const fabric = detail.materials.map(m => ({
        ...key, MaterialCD: s(m.materialCode), MaterialItemCD: s(m.materialAlias), Yardage: num(m.yardage), Width: s(m.width),
        ColorMatchPartCD: s(m.colorMatchPartCd), MaterialAlias: s(m.materialNm), MaterialMemo: s(m.materialMemo),
        UnitPrice: num(m.unitPrice), UsedPartCD: s(m.usedPartCd),
        TagColorCD: m.tagColorCd === 'com' ? '' : m.tagColorCd ? s(m.tagColorCd) : m.tagColors.map(c => s(c)).filter(Boolean).join('/'),
        DSGNK: s(m.vendorNm), SysGBN: sysGBN,
    }));
    const memo = detail.designMemos.flatMap(m => [m.contentKo, m.contentEn].filter(v => s(v)).map(v => ({ ...key, DesignMemo: s(v) })));
    const pattern = detail.stdSizes.flatMap(p => {
        // A graded matrix already contains the base size. Never duplicate it on the wire.
        const values = new Map(p.values.map(v => [v.sizeCd, v.value]));
        if (detail.stdSize && (p.size != null || !values.has(detail.stdSize)))
            values.set(detail.stdSize, p.size);
        return [...values].map(([sizeCd, value]) => ({
            ...key, Seq: String(p.seq), SizeCD: s(sizeCd), PatternPartCD: s(p.patternPartCd), PatternPartNm: s(p.patternPartNm),
            StdSize: s(detail.stdSize), Size: s(value), TorlnceL: s(p.tolLeft), TorlnceR: s(p.tolRight),
            Measurement: s(p.measurement), PlusTol: s(p.tolPlus), MinusTol: s(p.tolMinus),
        }));
    });
    const colorway = detail.colorways.filter(c => c.isUsed).flatMap(c => Object.entries(c.colors).filter(([, v]) => v.cd || v.nm).map(([part, v]) => ({
        ...key, ModelColorWaySeq: String(c.seq), TagColorCD: s(c.tagColorCd), ColorMatchPartCD: part, CoreColorNM: s(v.nm), StdColorCD: s(v.cd),
    })));
    const image = images.map(img => ({ ...key, AttachmentFilePath: s(img.url), FileSize: String(img.sizeBytes), ORGFILE: s(img.filename), IKIND: SP0070_IKIND[img.kind] ?? '', FileID: '' }));
    const trim = detail.subMaterials.flatMap(t => {
        const base = { ...key, SubMaterialCD: t.subMaterialCd ? s(resolveSubMaterialNumber(t.subMaterialCd)) : '',
            SubMaterialUsedPart: s(t.usedPart), TagColorCD: t.isCommon ? '' : t.tagColors.map(c => s(c.tagColorCd)).filter(Boolean).join('/'),
            SubMaterialMemo: s(t.contents), SubMaterialColorNm: t.isCommon ? '' : t.tagColors.map(c => s(c.colorNm)).filter(Boolean).join('/'), POSNR: posnrOf(t.seq) };
        const sizes = t.sizes.length ? t.sizes.filter(sz => sz.isUsed !== false) : [null];
        return sizes.map(sz => ({ ...base, Gauge: s(sz?.specText) || s(sz?.gauge), GaugeUnit: s(sz?.gaugeUnit),
            RealYardage: num(sz?.yardage), Unit: s(t.yardageUnit), SizeCD: sz ? s(sz.sizeCd) : 'com',
            Weight: num(sz?.realYardage), WeightUnit: s(t.yardageUnit), ZUNITID: '',
        }));
    });
    return orderedSp0070Payload({ ...head, MainDtl_Material: fabric, DesignMemo: memo, MainDtl_Pattern: pattern,
        MainDtl_ColorWay: colorway, DtlImg: image, ModelSubMaterial: trim });
}
//# sourceMappingURL=sap-transfer.js.map